// Ultimate Card Game (UCG) - Global Error Reporter
//
// The game loop's own try/catch (js/main.js's gameLoop()) only ever sees
// errors thrown from update()/render() themselves. Everything else - click/
// drag/hotkey handlers, network callbacks, any other DOM-triggered code
// that runs outside a simulation tick - throws with nobody watching, which
// is exactly the class of failure that reads as an unexplained "crash":
// the rAF chain survives (gameLoop() reschedules itself before running the
// tick), but whatever the player just did silently did nothing.
//
// This module closes that gap with two independent pieces:
//   1. Global 'error'/'unhandledrejection' listeners catch what the game
//      loop's try/catch can't.
//   2. A small ring buffer of the last MAX_ENTRIES errors (with a cheap
//      snapshot of what the game was doing) persisted to localStorage - so
//      a crash hit on a phone, with no devtools attached, leaves something
//      inspectable afterwards. Read it back with ErrorReporter.readLog().
//
// Deliberately independent of HUD/canvas: the banner it shows is a fixed-
// position sibling of #game-wrapper (see index.html), built and toggled
// entirely by this module, so it stays reachable even if the very thing
// that broke was rendering/layout itself.
//
// Never touches gameplay state - purely observational.

const STORAGE_KEY = 'ucg_error_log';
const MAX_ENTRIES = 20;

export class ErrorReporter {
  constructor(game) {
    this.game = game;
    this.shownCount = 0;

    window.addEventListener('error', (e) => {
      this.report(e.error || e.message, 'window.onerror');
    });
    window.addEventListener('unhandledrejection', (e) => {
      this.report(e.reason, 'unhandledrejection');
    });
  }

  // A few cheap primitives, not a full state dump - safe to read from
  // inside an error handler itself, and enough to tell "online match mid-
  // turn" from "single-player, paused" when reading the log back later.
  context() {
    const g = this.game;
    return {
      isOnline: !!g?.isOnline,
      isPaused: !!g?.isPaused,
      isRunning: !!g?.isRunning,
      myTeam: g?.myTeam,
      frameErrorCount: g?.frameErrorCount || 0
    };
  }

  report(errorOrMessage, source) {
    const message = errorOrMessage?.message || String(errorOrMessage);
    // Benign browser notifications that don't indicate an actual runtime error
    if (typeof message === 'string' && (
      message.includes('ResizeObserver loop') ||
      message.includes('Failed to load because no supported source was found') ||
      message.includes('play() failed') ||
      message.includes('The play() request was interrupted') ||
      message.includes('exponentialRampToValueAtTime') ||
      message.includes('AudioParam')
    )) {
      console.warn(`[UCG] Ignored benign browser/media notice (${source}):`, errorOrMessage);
      return;
    }
    const errName = errorOrMessage?.name || '';
    if (errName === 'NotSupportedError' || errName === 'NotAllowedError' || errName === 'AbortError') {
      console.warn(`[UCG] Ignored media DOMException (${source}):`, errorOrMessage);
      return;
    }
    const stack = errorOrMessage?.stack || null;
    const entry = { time: new Date().toISOString(), source, message, stack, context: this.context() };

    console.error(`[UCG] Unhandled error (${source}):`, errorOrMessage, entry.context);
    this._persist(entry);
    this._showBanner();
  }

  _persist(entry) {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const log = raw ? JSON.parse(raw) : [];
      log.push(entry);
      while (log.length > MAX_ENTRIES) log.shift();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
    } catch {
      // localStorage unavailable/full/private-mode - the console.error
      // above already ran, so there's nothing further to do here.
    }
  }

  // Idempotent by design: a burst of errors from the same broken handler
  // (e.g. a listener that throws on every pointermove) must not spam the
  // player with repeated banners - the banner just stays up and its count
  // ticks, rather than re-animating in per error.
  _showBanner() {
    this.shownCount++;
    const banner = document.getElementById('fatal-error-banner');
    const textEl = document.getElementById('fatal-error-banner-text');
    if (!banner || !textEl) return;

    textEl.textContent = this.shownCount > 1
      ? `Something went wrong (${this.shownCount}x). Reload if the game seems stuck.`
      : 'Something went wrong. Reload if the game seems stuck.';
    banner.classList.remove('hidden');

    if (!this._reloadWired) {
      this._reloadWired = true;
      document.getElementById('fatal-error-banner-reload')
        ?.addEventListener('click', () => window.location.reload());
    }
  }

  static readLog() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
}
